import CowbellFactorsMapping from '../../../utility/CowbellFactorsMapping';
import type {
  FactorRangeAggregate,
  FactorRangeDatum,
} from './factorRange.types';
import type { FactorTrends } from './factorTrends';

const AGGREGATE_KEY = 'companyAggregate';

// The aggregate is promoted into the panel header, the industry aggregate has
// no company score to plot, and Insider Threat is never customer-facing.
const EXCLUDED_KEYS = new Set([
  AGGREGATE_KEY,
  'industryAggregate',
  'insiderThreat',
  'insiderThreatCF',
]);

interface RiskFactorEntry {
  readonly key: string;
  readonly label?: string;
  readonly tooltip?: string;
  readonly company?: number;
  readonly industry?: number;
  readonly min?: number;
  readonly max?: number;
}

interface FactorRangeData {
  readonly aggregate: FactorRangeAggregate | null;
  readonly factors: FactorRangeDatum[];
}

const isNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/** A row needs all four markers; a partial one would plot in the wrong place. */
const isPlottable = (entry: RiskFactorEntry): boolean =>
  isNumber(entry.company) &&
  isNumber(entry.industry) &&
  isNumber(entry.min) &&
  isNumber(entry.max);

/**
 * Shapes the customer risk payload into the rows and header aggregate the
 * shared-axis panel renders, folding in each factor's trend delta.
 */
export function toFactorRangeData(
  riskData: readonly RiskFactorEntry[] | undefined,
  trends: FactorTrends
): FactorRangeData {
  if (!riskData?.length) {
    return { aggregate: null, factors: [] };
  }

  const aggregateEntry = riskData.find((entry) => entry.key === AGGREGATE_KEY);
  const aggregate =
    aggregateEntry && isPlottable(aggregateEntry)
      ? {
          company: aggregateEntry.company as number,
          industry: aggregateEntry.industry as number,
          min: aggregateEntry.min as number,
          max: aggregateEntry.max as number,
          trend: trends[AGGREGATE_KEY],
        }
      : null;

  const factors = riskData
    .filter((entry) => !EXCLUDED_KEYS.has(entry.key) && isPlottable(entry))
    .map((entry) => ({
      factorKey: entry.key,
      label:
        entry.label ?? CowbellFactorsMapping[entry.key]?.label ?? entry.key,
      subtext: CowbellFactorsMapping[entry.key]?.subtext,
      tooltip: entry.tooltip,
      company: entry.company as number,
      industry: entry.industry as number,
      min: entry.min as number,
      max: entry.max as number,
      trend: trends[entry.key],
    }));

  return { aggregate, factors };
}

export default toFactorRangeData;
